import { NoteSourceArtifactInfo } from "@/components/note/editor/source-bar/types";
import { WebAudioArtifactState } from "@/modules/audio-recording/web-artifacts";
import { toastModule } from "@/modules/toast";
import { AudioRecordingObservable } from "@/store/audio-recordings";
import { getFileExtensionFromMimeType } from "@/modules/audio-recording/upload/mime-utils";
import { prepareAudioRecordingDownloadUrl } from "@/modules/audio-recording/prepare-download";
import { buildAudioRecordingDownloadFileName } from "@/modules/audio-recording/download-filename";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import type { Uuid } from "@/domains/global/identifiers";

export const downloadRecordingAudio = async ({
  recording,
}: {
  recording: AudioRecordingObservable | null;
}) => {
  if (!recording) return;

  const noteTitle = await getNoteTitleForRecording(recording);
  if (recording.recordingStrategy === "desktop-artifact") {
    await downloadRecordingMp3({ audioRecordingId: recording.id, noteTitle });
    return;
  }

  const artifactSnapshot =
    await recording.store.dexie.repositories.webAudioArtifacts.getAudioArtifactSnapshot({
      audioRecordingId: recording.id,
    });
  if (artifactSnapshot?.state !== WebAudioArtifactState.Finalized) {
    toastModule.triggerToast({ content: "Recording is not ready to download" });
    return;
  }

  const blob = await recording.store.dexie.repositories.webAudioArtifacts.readAudioArtifactBlob({
    artifactRef: artifactSnapshot,
  });
  await saveBlob(blob, artifactSnapshot.mimeType, noteTitle);
};

export const downloadRecordingMp3 = async ({
  audioRecordingId,
  downloadUrl,
  noteTitle,
}: {
  audioRecordingId: Uuid | null;
  downloadUrl?: string | null;
  noteTitle?: string | null;
}) => {
  if (!audioRecordingId) return;

  try {
    const url =
      downloadUrl ??
      (await prepareAudioRecordingDownloadUrl({ audioRecordingId: String(audioRecordingId) }));
    if (!url) {
      throw new Error("Missing download URL");
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download audio status: ${response.status}`);
    }
    const audioBlob = await response.blob();
    await saveBlob(audioBlob, "audio/mp3", noteTitle);
  } catch (error) {
    logger.error({
      message: "Failed to download audio",
      info: {
        audioRecordingId: String(audioRecordingId),
        error: objectModule.safeErrorAsJson(error as Error),
      },
    });
    toastModule.triggerToast({ content: "Failed to download audio" });
  }
};

export const downloadArtifactAudio = async ({
  artifactInfo,
}: {
  artifactInfo: NoteSourceArtifactInfo;
}) => {
  if (artifactInfo?.kind !== "AUDIO" || !artifactInfo.data) return;

  const url = artifactInfo.data.download_url;
  if (!url) return;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download audio status: ${response.status}`);
    }
    const audioBlob = await response.blob();
    await saveBlob(audioBlob, artifactInfo.data.mime_type);
  } catch (error) {
    logger.error({
      message: "Failed to download audio",
      info: {
        url,
        error: objectModule.safeErrorAsJson(error as Error),
      },
    });
    toastModule.triggerToast({ content: "Failed to download audio" });
  }
};

const saveBlob = async (blob: Blob | null, mimeTypeStr?: string, noteTitle?: string | null) => {
  if (!blob || !blob.size) {
    toastModule.triggerToast({ content: "Recording is empty" });
    return;
  }
  const audioUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = audioUrl;
  const extension = getFileExtensionFromMimeType({ mimeTypeStr: mimeTypeStr ?? blob.type });
  a.download = buildAudioRecordingDownloadFileName({ noteTitle, extension });
  a.click();

  // Calculate timeout based on file size: ~10ms per MB, minimum 500ms, maximum 5s
  // For a 200 MB file, this gives ~2s which should be sufficient
  const timeoutMs = Math.min(5_000, Math.max(500, (blob.size / 1_000_000) * 10));
  setTimeout(() => {
    // Clean up the object URL after download starts or fails
    URL.revokeObjectURL(audioUrl);
  }, timeoutMs);
  toastModule.triggerToast({ content: "Recording saved" });
};

const getNoteTitleForRecording = async (
  recording: AudioRecordingObservable
): Promise<string | null> => {
  const noteId = recording.associatedNoteId as Uuid | undefined;
  if (!noteId) return null;

  const cachedNote = recording.store.notes.getNoteObservableById({ noteId });
  if (cachedNote?.title) return cachedNote.title;

  const note = await recording.store.notes.getAsync(noteId);
  return note?.title ?? null;
};
